import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { postKnowledgebase, type BedrockApiResponse } from 'queries';
import { RootStore } from 'store';

export type ChatMessage = {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  sourceLinks?: Array<{ title: string; url: string }>;
  action?: {
    type?: string;
    payload: {
      title?: string;
      phone?: string;
      customerID?: string;
      jobID?: string;
      url?: string;
      msg?: string;
    };
  };
};

export function useAiAssistant() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ChatMessage[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [suggestedChips, setSuggestedChips] = useState([
    { id: '1', text: 'Why hasn\'t my ‘Conversion Rate’ metric been updated?' },
    { id: '2', text: 'What is the GeekSkill ‘Tech & Ticket Nerd’?' },
    { id: '3', text: 'How do I process a refund for a customer?' },
    { id: '4', text: 'Where can I find the latest pricing policy?' },
  ]);

  const { customerProfile, job } = RootStore;

  const { mutate: askAssistant, isLoading: loading } = useMutation(
    async (queryText: string) => {
      const payload = {
        query: queryText,
        session_id: sessionId,
        customer_ID: customerProfile?.ID,
      };
      return await postKnowledgebase(payload);
    },
    {
      onSuccess: (data: BedrockApiResponse) => {
        if (data.error) throw new Error(data.error);

        if (data.session_id) {
          setSessionId(data.session_id);
        }

        const extractedSources: Array<{ title: string; url: string }> = [];
        if (data.citations) {
          data.citations.forEach((citation) => {
            citation.retrievedReferences?.forEach((ref) => {
              if (ref.metadata?.title && ref.metadata?.url) {
                if (!extractedSources.some((s) => s.url === ref.metadata?.url)) {
                  extractedSources.push({
                    title: ref.metadata.title,
                    url: ref.metadata.url,
                  });
                }
              }
            });
          });
        }

        const assistantMsg: ChatMessage = {
          id: `ai_${Date.now()}`,
          sender: 'assistant',
          text: data.answer.replace('n **', '\n\n**'),
          sourceLinks: extractedSources,
          action: {
            type: data.next_action?.type,
            payload: {
              title: data.next_action?.payload?.title,
              phone: data.next_action?.payload?.phone,
              customerID: data.next_action?.payload?.customerID,
              jobID: data.next_action?.payload?.jobID,
              url: data.next_action?.payload?.url,
              msg: data.next_action?.payload?.msg,
            },
          },
        };

        setConversation((prev) => [...prev, assistantMsg]);
      },
      onError: (err: any) => {
        setErrorMsg(err.message || 'An unexpected error occurred while communicating with the AI.');
      },
    }
  );

  const handlePostQuery = (queryText: string) => {
    const textToSubmit = queryText.trim();
    if (!textToSubmit) return;

    setErrorMsg(null);
    const userMsg: ChatMessage = { id: `user_${Date.now()}`, sender: 'user', text: textToSubmit };
    setConversation((prev) => [...prev, userMsg]);

    askAssistant(textToSubmit);
  };

  const handleDismissChip = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSuggestedChips((prev) => prev.filter((chip) => chip.id !== id));
  };

  const handleClearChat = () => {
    setConversation([]);
    setSessionId(null);
    setErrorMsg(null);
  };

  return {
    loading,
    conversation,
    errorMsg,
    suggestedChips,
    customerProfile,
    job,
    handlePostQuery,
    handleDismissChip,
    handleClearChat,
  };
}
